import { isActive } from '@hoopoe/shared';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Activity as ActivityIcon } from 'lucide-react';
import { ProgressCard } from '../components/activity/progress-card';
import { EmptyState } from '../components/guidance/empty-state';
import { PageHeader } from '../components/layout/app-shell';
import { Button } from '../components/ui/button';
import { useActivity } from '../lib/activity';

export const Route = createFileRoute('/activity')({ component: ActivityPage });

function ActivityPage() {
  const { jobs, now, cancel, retry, connected } = useActivity();
  const env = useQuery({ queryKey: ['health'], queryFn: async () => (await fetch('/api/health')).json() as Promise<{ env: string }> });
  const simulate = (mode: 'ok' | 'stall' | 'fail') =>
    fetch('/api/activity/simulate', { method: 'POST', body: JSON.stringify({ mode, stepMs: mode === 'stall' ? 400 : 1200 }) });
  const active = jobs.filter(isActive);
  const recent = jobs.filter(j => !isActive(j));
  return (
    <div className="space-y-6">
      <PageHeader
        title="Activity"
        description={connected ? 'Live: updates stream as jobs run.' : 'Reconnecting…'}
        actions={
          env.data && env.data.env !== 'production' ? (
            <fieldset className="flex gap-2">
              <legend className="sr-only">Simulate a job</legend>
              <Button variant="outline" size="sm" onClick={() => simulate('ok')}>
                Simulate job
              </Button>
              <Button variant="outline" size="sm" onClick={() => simulate('stall')}>
                Simulate stall
              </Button>
              <Button variant="outline" size="sm" onClick={() => simulate('fail')}>
                Simulate failure
              </Button>
            </fieldset>
          ) : null
        }
      />
      {jobs.length === 0 && (
        <EmptyState icon={ActivityIcon} title="Nothing running" learnMore="progress-and-activity">
          Uploads, imports, AI re-checks and reports show their progress here while they run.
        </EmptyState>
      )}
      {active.length > 0 && (
        <section aria-labelledby="running" className="space-y-3">
          <h2 id="running" className="text-sm font-semibold text-muted-foreground">
            Running
          </h2>
          {active.map(j => (
            <ProgressCard key={j.id} job={j} now={now} onCancel={() => cancel(j.id)} />
          ))}
        </section>
      )}
      {recent.length > 0 && (
        <section aria-labelledby="recent" className="space-y-3">
          <h2 id="recent" className="text-sm font-semibold text-muted-foreground">
            Recent
          </h2>
          {recent.map(j => (
            <ProgressCard key={j.id} job={j} now={now} onCancel={() => cancel(j.id)} onRetry={() => retry(j.id)} />
          ))}
        </section>
      )}
    </div>
  );
}
